import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/components/ui/use-toast";
import { Capacitor } from '@capacitor/core';

// Key for storing the last path in localStorage (same as in useAuth.tsx)
const LAST_PATH_KEY = 'metefy_last_path';
// Key for storing redirect path after login
const REDIRECT_AFTER_LOGIN_KEY = 'metefy_redirect_after_login';
// Key for storing OAuth source
const OAUTH_SOURCE_KEY = 'metefy_oauth_source';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [message, setMessage] = useState("Completing sign in...");
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // DISABLED: Mobile app redirect logic removed per user request
    // OAuth should always stay in browser, never redirect to mobile app
    // Only redirect if explicitly marked as from_app AND actually in native app
    // const isFromApp = searchParams.get('from_app') === 'true' && Capacitor.isNativePlatform();
    
    // if (isFromApp) {
    //   // Get all URL parameters (both hash and query)
    //   const hash = window.location.hash || '';
    //   const search = window.location.search || '';
    //   
    //   // Log for debugging
    //   console.log('[AuthCallback] Full URL:', window.location.href);
    //   console.log('[AuthCallback] Hash:', hash);
    //   console.log('[AuthCallback] Search:', search);
    //   
    //   let fullParams = '';
    //   if (hash) {
    //     fullParams = hash.startsWith('#') ? hash : `#${hash}`;
    //   } else if (search) {
    //     fullParams = search.startsWith('?') ? search : `?${search}`;
    //   }
    //   
    //   const customSchemeUrl = `metefy://auth/callback${fullParams}`;
    //   console.log('[AuthCallback] Redirecting to app:', customSchemeUrl);
    //   
    //   // For Android, use Intent URL which is more reliable than custom scheme
    //   // This tells Chrome to open the app directly
    //   const intentUrl = `intent://auth/callback${fullParams}#Intent;scheme=metefy;package=com.metefy.app;end`;
    //   
    //   setMessage("Redirecting back to Metefy app...");
    //   
    //   // Try multiple methods to open the app
    //   const tryOpenApp = () => {
    //     // Method 1: Try Android Intent URL first (most reliable on Android)
    //     const isAndroid = /android/i.test(navigator.userAgent);
    //     if (isAndroid) {
    //       console.log('[AuthCallback] Using Android Intent URL');
    //       window.location.href = intentUrl;
    //     } else {
    //       // Method 2: Use custom scheme for iOS or other platforms
    //       console.log('[AuthCallback] Using custom scheme URL');
    //       window.location.href = customSchemeUrl;
    //     }
    //   };
    //   
    //   // Execute immediately
    //   tryOpenApp();
    //   
    //   // Fallback: if still in browser after 2 seconds, show manual button
    //   setTimeout(() => {
    //     if (document.hasFocus()) {
    //       console.log('[AuthCallback] Still in browser, showing manual option');
    //       setError("Tap the button below to open the Metefy app");
    //       setMessage("Authentication successful!");
    //       setIsProcessing(false);
    //     }
    //   }, 2000);
    //   
    //   return;
    // }

    // Add a small delay to allow the code parameter to be properly captured
    const checkAuth = setTimeout(() => {
      const code = searchParams.get('code');
      const hash = window.location.hash || '';
      const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      // Log for debugging
      console.log('[AuthCallback] Code:', code);
      console.log('[AuthCallback] Access token:', accessToken ? 'present' : 'missing');
      console.log('[AuthCallback] Refresh token:', refreshToken ? 'present' : 'missing');

      if (accessToken && refreshToken) {
        // Implicit flow
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })
        .then(({ data, error }) => {
          if (error) {
            console.error('[AuthCallback] Session error:', error);
            setError(error.message);
            throw error;
          }
          setMessage("Sign in successful! Redirecting...");
          // Check localStorage for OAuth source to determine redirect path
          const source = localStorage.getItem(OAUTH_SOURCE_KEY);
          let redirectPath = '/profile'; // Default to profile
          
          if (source === 'hero' || source === 'found-items-map') {
            // Coming from "Get Started" button or Found Items Map - go to found items map
            redirectPath = '/found-items-map';
          }
          
          // Clean up localStorage (but keep pending_action for FoundItemsMap to handle)
          localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
          localStorage.removeItem(OAUTH_SOURCE_KEY);
          
          setTimeout(() => navigate(redirectPath, { replace: true }), 2000);
        })
        .catch((error) => {
          console.error('[AuthCallback] Error:', error);
          // Clean up localStorage on error
          localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
          localStorage.removeItem(OAUTH_SOURCE_KEY);
          setError(error.message || "Failed to complete sign in");
          toast({
            title: "Authentication Error",
            description: error.message || "Failed to complete sign in",
            variant: "destructive",
          });
          setTimeout(() => navigate('/auth', { replace: true }), 3000);
        })
        .finally(() => setIsProcessing(false));
        return;
      }

      if (code) {
        // PKCE flow
        supabase.auth.exchangeCodeForSession(code)
          .then(({ data, error }) => {
            if (error) {
              console.error('[AuthCallback] Exchange error:', error);
              setError(error.message);
              throw error;
            }
            if (data.session && data.user) {
              setMessage("Sign in successful! Redirecting...");
              // Check localStorage for OAuth source to determine redirect path
              const source = localStorage.getItem(OAUTH_SOURCE_KEY);
              let redirectPath = '/profile'; // Default to profile
              
              if (source === 'hero' || source === 'found-items-map') {
                // Coming from "Get Started" button or Found Items Map - go to found items map
                redirectPath = '/found-items-map';
              }
              
              // Clean up localStorage (but keep pending_action for FoundItemsMap to handle)
              localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
              localStorage.removeItem(OAUTH_SOURCE_KEY);
              
              setTimeout(() => navigate(redirectPath, { replace: true }), 2000);
            } else {
              throw new Error("No session returned");
            }
          })
          .catch((error) => {
            console.error('[AuthCallback] Error:', error);
            // Clean up localStorage on error
            localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
            localStorage.removeItem(OAUTH_SOURCE_KEY);
            setError(error.message || "Failed to complete sign in");
            toast({
              title: "Authentication Error",
              description: error.message || "Failed to complete sign in",
              variant: "destructive",
            });
            setTimeout(() => navigate('/auth', { replace: true }), 3000);
          })
          .finally(() => setIsProcessing(false));
        return;
      }

      // No code or token found
      console.error('[AuthCallback] No auth parameters found. URL:', window.location.href);
      setError("Authentication code not found in URL");
      toast({
        title: "Authentication Issue",
        description: "Authentication code not found. Please try signing in again.",
        variant: "destructive",
      });
      setMessage("Authentication code not found. Redirecting...");
      navigate('/auth', { replace: true });
      setIsProcessing(false);
    }, 1000); // 1000ms delay to ensure URL parameters are properly loaded

    return () => clearTimeout(checkAuth);
  }, [navigate, searchParams, toast]);

  // Function to manually open the app
  const openApp = () => {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    let fullParams = '';
    if (hash) {
      fullParams = hash.startsWith('#') ? hash : `#${hash}`;
    } else if (search) {
      fullParams = search.startsWith('?') ? search : `?${search}`;
    }
    
    const isAndroid = /android/i.test(navigator.userAgent);
    if (isAndroid) {
      const intentUrl = `intent://auth/callback${fullParams}#Intent;scheme=metefy;package=com.metefy.app;end`;
      window.location.href = intentUrl;
    } else {
      const customSchemeUrl = `metefy://auth/callback${fullParams}`;
      window.location.href = customSchemeUrl;
    }
  };

  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="text-center max-w-md px-4">
        <h2 className="text-xl font-semibold mb-2">{message}</h2>
        {error && !isProcessing && searchParams.get('from_app') === 'true' && (
          <div className="mt-4">
            <button
              onClick={openApp}
              className="w-full bg-sky-700 hover:bg-sky-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Open Metefy App
            </button>
            <p className="text-gray-500 text-sm mt-2">
              If the app doesn't open, please open it manually.
            </p>
          </div>
        )}
        {error && !searchParams.get('from_app') && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-medium">Error:</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        )}
        <p className="text-gray-600 mt-2">
          {isProcessing ? "Please wait while we process your authentication..." : ""}
        </p>
      </div>
    </div>
  );
} 